import { createListenerMiddleware, isAnyOf, nanoid } from '@reduxjs/toolkit';
import type { ListenerEffectAPI, SerializedError, TypedStartListening } from '@reduxjs/toolkit';
import { toast } from 'sonner';
import { EQuestionType, TEMPLATE_PERMISSIONS, QUESTION_PERMISSIONS } from '@hubertai/screening-question-types';
import { computeRuleChangePayload } from './computeRuleChangePayload';
import type { AppDispatch, RootState } from './store';
import { getNodeRules } from 'hooks/useQuestionRules';
import {
  setCurrentQuestionLanguage,
  updateLibraryQuestionOptionEvaluation,
  updateLibraryQuestionRules,
  updateSwapOptionsToLibrary,
} from 'rtk/LibrarySlice';
import { questionsApi } from 'rtk/query/apis/questions';
import {
  addBaseBlock,
  addInterviewSummaryBlock,
  addNodeToBlock,
  addQuestionToBlock,
  addQuickMessageToBlock,
  addTemplate,
  applyAutomaticKnockout,
  customParametersUpdate,
  customQuestionRuleActionPlus,
  customQuestionRuleConditionPlus,
  customQuestionRuleUpdate,
  deleteAllCustomRules,
  deleteBlock,
  deleteCustomQuestionRuleByIndex,
  deleteNodeFromBlock,
  deleteNodeParameterData,
  deleteQuickMessageFromBlock,
  editBlockName,
  editTemplateRoleId,
  editTemplateRoleName,
  ETemplateRevision,
  formParametersUpdateByFormId,
  pushToChangeLog,
  removeQuestionFromBlock,
  resetCustomEvaluation,
  setCurrentTemplateLanguage,
  setCustomEvaluation,
  updateBaseBlocks,
  updateCategoryWeight,
  updateInterviewSummaryBlockName,
  updateInterviewSummaryBlocks,
  updateInterviewSummaryDetailLabelByQuestionId,
  updateProcessId,
  updateQuestionInterviewSummaryBlock,
  updateQuickMessageText,
  updateScoringCategories,
  updateSelectionCountCriteria,
  updateTemplate,
  updateTemplateAllowedLanguageList,
  updateTemplateCompanyName,
  updateTemplateEstimatedLength,
  updateTemplateSalaryRange,
  updateTemplateLanguageList,
  updateTemplateMyHubertFlag,
  updateOptionEnabledInParameters,
  setReplyOptionsInParameters,
  updateTemplateEmailTemplateOverride,
  updateTemplateSmsInvitation,
  updateTemplateVoiceSettings,
  updateTemplateInterviewBaseUrl,
  addTemplateReminderSetting,
  updateTemplateReminderSettingByIndex,
  deleteTemplateReminderSettingByIndex,
  updateTemplateFeatureFlags,
} from 'rtk/TemplateSlice';
import { templatesApi } from 'rtk/query/apis/templates';
import { EEvaluationType } from 'types/evaluation.types';
import type { TTemplateChangeLogItem } from 'types/template/template.types';
import type { TRecruiterAppUser } from 'types/user.types';
import { isRecruiterUser } from 'types/user.types';
import { toastRuleReferenceBlockedError } from 'utils/toastRuleReferenceBlockedError';
import type { TUpdatedTemplateResponse } from 'rtk/query/apis/templates/templates.api.types';
import type { TAxiosBaseResponseError } from 'rtk/query/rtk-query.types';

enum EMiddlewareExitCodes {
  Ok = 'ok',
  DraftExists = 'draft-exists',
}

type TPreReducerMiddleReturn = {
  code: EMiddlewareExitCodes;
  draftId?: string;
  templateId?: string;
};

const allowedRecruiterActions = [
  'setCurrentTemplateLanguage',
  'addTemplate',
  'setCurrentQuestion',
  'updateAccessGroupsVisibilityAndClientId',
  'pushToChangeLog',
];

const isAllowedRecruiterAction = (actionName?: string) => {
  return allowedRecruiterActions.includes(actionName ?? '');
};

const hasTemplatePermission = (user: TRecruiterAppUser, template: any) =>
  user.access_groups_permissions?.some(
    (ag) =>
      ag.permissions.includes(TEMPLATE_PERMISSIONS.Update) &&
      ag.resources.some(
        (r) =>
          (r.resourceId === template.templateReferenceId || r.resourceId === template.cTemplateId) &&
          r.type === 'template',
      ),
  ) ||
  user.access_groups_permissions?.some((ag) => ag.permissions.includes(TEMPLATE_PERMISSIONS.ManageClientLevel)) ||
  user.access_groups_permissions?.some(
    (ag) =>
      ag.permissions.includes(TEMPLATE_PERMISSIONS.ShareOrg) &&
      template.visibility === 'client' &&
      ag.permissions.includes(TEMPLATE_PERMISSIONS.Update),
  );

const hasQuestionPermission = (user: TRecruiterAppUser, question: any) =>
  user.access_groups_permissions?.some(
    (ag) =>
      ag.permissions.includes(QUESTION_PERMISSIONS.Update) &&
      ag.resources.some((r) => r.resourceId === question.questionId && r.type === 'question'),
  ) ||
  user.access_groups_permissions?.some(
    (ag) =>
      ag.permissions.includes(QUESTION_PERMISSIONS.ShareOrg) &&
      question.visibility === 'client' &&
      ag.permissions.includes(QUESTION_PERMISSIONS.Update),
  );

const getActionTypeAndName = (action?: { type: string }) => ({
  actionType: action?.type,
  actionName: action?.type?.split('/')[1],
});

const isTemplateAction = (actionType?: string) =>
  actionType && typeof actionType === 'string' && actionType.includes('Template/');

const isQuestionAction = (actionType?: string) =>
  actionType && typeof actionType === 'string' && actionType.includes('Question/');

export const preReducerMiddleware =
  (store: any) =>
  (next: any) =>
  (action: any): TPreReducerMiddleReturn | any => {
    const state = store.getState();
    const user = state.config.user;
    const { actionName, actionType } = getActionTypeAndName(action);

    if (isTemplateAction(actionType)) {
      const template = state.Template.present;

      if (isRecruiterUser(user) && !isAllowedRecruiterAction(actionName)) {
        if (hasTemplatePermission(user, template) === true) {
          return next(action);
        }
        toast.error('You do not have permission to do updates.');
        return;
      }

      if (template.revision !== ETemplateRevision.Draft && !isAllowedRecruiterAction(actionName)) {
        toast.error('There is already an existing draft.');
        return;
      }
    } else if (isQuestionAction(actionType)) {
      if (isRecruiterUser(user) && !isAllowedRecruiterAction(actionName)) {
        const question = state.Question;
        if (hasQuestionPermission(user, question) === true) {
          return next(action);
        }
        toast.error('You do not have permission to do updates.');
        return;
      }
    }

    const result = next(action);
    return result;
  };

/**
 * Remember: REDUCER RUNS FIRST!
 * effect after.
 */

const appMiddleware = createListenerMiddleware();

/**
 * Add question side effects
 * - Add to question list
 * - Add to outline
 * - Add to summary block
 */

appMiddleware.startListening({
  matcher: isAnyOf(addNodeToBlock, addQuestionToBlock),
  effect(action: any, listenerApi: any) {
    const state = listenerApi.getState();
    const Library = state.Library;
    const resource = action.payload.resource;

    const nodeId = resource.newNode.id;
    const node = Library[nodeId];
    if (node && node.type === EQuestionType.Open && node.scoring_category && node.weight) {
      // Add scoring category to list
      listenerApi.dispatch(
        updateScoringCategories({
          nodeId,
          scoringCategory: node.scoring_category,
          weight: node.weight,
        }),
      );
    }
  },
});

/**
 * Delete question side effects
 * - Remove from question list
 * - Remove from block or rules
 * - Remove from outline
 * - Remove from summary block
 */

appMiddleware.startListening({
  matcher: isAnyOf(deleteNodeFromBlock),
  effect(action: any, listenerApi) {
    const state = listenerApi.getState();

    const resource = action.payload.resource;
    const nodeId = resource.nodeId;

    function _deleteNode(
      state: any, //RootState,
      api: any, // ListenerEffectAPI,
      nodeId: string,
    ) {
      const Library = state.Library;
      const questionConfig = Library[nodeId];
      if (!questionConfig) {
        toast.error('Delete node from block middleware error. Reload and try again, otherwise contact support.');
        return;
      }

      // Remove any custom parameter data
      listenerApi.dispatch(deleteNodeParameterData({ nodeId }));
      // Remove any custom evaluation data

      const nodeParameters = state.Template?.present?.c?.parameters[nodeId];
      const rules = getNodeRules(nodeParameters, questionConfig.rules);
      console.log(`delete node ${nodeId}: rules`, rules);
      rules.list.map((rule) => {
        if (rule.actions && rule.actions.length) {
          rule.actions.map((action) => {
            action.value.map((v) => {
              if (typeof v === 'string') {
                _deleteNode(state, api, v);
              } else {
                _deleteNode(state, api, v.id);
              }
            });
          });
        }
      });
    }

    _deleteNode(state, listenerApi, nodeId);
  },
});

/**
 * Update template Side Effects
 */
const startListening = appMiddleware.startListening as TypedStartListening<RootState, AppDispatch>;
type ListenerApi = ListenerEffectAPI<RootState, AppDispatch>;
type Dispatcher = Pick<ListenerApi, 'dispatch'>;

startListening({
  matcher: isAnyOf(
    /******* General Functionality *******/
    updateTemplate,
    updateTemplateAllowedLanguageList,
    updateTemplateLanguageList,
    editTemplateRoleId,
    editTemplateRoleName,
    updateTemplateMyHubertFlag,
    updateTemplateCompanyName,
    updateTemplateEstimatedLength,
    updateTemplateSalaryRange,
    updateTemplateFeatureFlags,
    resetCustomEvaluation,
    /******* EMAIL / SMS / SETTINGS *******/
    updateTemplateEmailTemplateOverride,
    updateTemplateSmsInvitation,
    updateTemplateVoiceSettings,
    updateTemplateInterviewBaseUrl,
    addTemplateReminderSetting,
    updateTemplateReminderSettingByIndex,
    deleteTemplateReminderSettingByIndex,
    /******* TEMPLATE BLOCKS *******/
    editBlockName,
    addBaseBlock,
    updateBaseBlocks,
    deleteBlock,
    addNodeToBlock,
    deleteNodeFromBlock,
    addQuickMessageToBlock,
    updateQuickMessageText,
    deleteQuickMessageFromBlock,
    /******* TEMPLATE INTERVIEW SUMMARY *******/
    updateInterviewSummaryDetailLabelByQuestionId,
    updateQuestionInterviewSummaryBlock,
    updateInterviewSummaryBlockName,
    addInterviewSummaryBlock,
    updateInterviewSummaryBlocks,
    /******* TEMPLATE PARAMETERS *******/
    formParametersUpdateByFormId,
    updateOptionEnabledInParameters,
    setReplyOptionsInParameters,
    // Rule edits (customQuestionRuleUpdate, deleteCustomQuestionRuleByIndex, deleteAllCustomRules,
    // customQuestionRuleConditionPlus, customQuestionRuleActionPlus) have their
    // own listener below — they route through apply-rule-change instead of the generic update, so
    // the backend (not this middleware) decides whether each edit lands on the question directly
    // or as a template-scoped override.
    /******* EVALUATION *******/
    // setCustomEvaluation has its own listener below too — select-option evaluation edits route
    // through apply-evaluation-change the same way rule edits do; every other evaluation kind
    // (matrix/slider/etc.) still falls through to performGenericTemplateUpdate from there.
    customParametersUpdate,
    updateCategoryWeight,
    updateSelectionCountCriteria,
  ),
  async effect(action: any, listenerApi) {
    await performGenericTemplateUpdate(action, listenerApi);
  },
});

// Shared by the generic matcher above and setCustomEvaluation's own listener below (for
// nodeTypes that don't get the eligibility-routed evaluation path) — blindly overwrites the
// template with the current local state, the original save behavior every action here used before
// any of it was routed through a narrower, backend-decided endpoint.
async function performGenericTemplateUpdate(action: any, listenerApi: ListenerApi) {
  const state = listenerApi.getState();
  const revision = state.Template.present.revision;

  if (revision !== ETemplateRevision.Draft) {
    toast.error('Updates are only allowed on draft templates.');
    return;
  }

  const payload = action.payload;

  const user = state.config.user;
  const item: TTemplateChangeLogItem = {
    id: nanoid(),
    created_at: new Date().toISOString(),
    actor: 'user',
    actor_id: user!._id,
    actor_username: user!.email,
    action_type: payload.eventType,
    action_id: action.type,
    message: payload.description,
  };

  if ('field_changed' in payload) {
    item.field_changed = payload.field_changed;
  }

  if ('old_value' in payload) {
    item.old_value = payload.old_value;
  }

  if ('new_value' in payload) {
    item.new_value = payload.new_value;
  }

  if ('value' in payload) {
    item.value = payload.resource;
  }

  const x = await listenerApi.dispatch(
    templatesApi.endpoints.updateTemplateById.initiate({
      templateId: state.Template.present.cTemplateId,
      templateReferenceId: state.Template.present.templateReferenceId,
      template: state.Template.present.c,
      revision: state.Template.present.revision,
      processId: state.Template.present.processId,
      changeLogItem: item,
      accessGroupIds: state.Template.present.c.accessGroupIds,
    }),
  );

  if (x.error) {
    toast.error(getErrorMessage(x.error));
    return;
  } else if (x.data && x.data.template.process_id) {
    refreshProcessId(x.data, listenerApi);

    listenerApi.dispatch(
      pushToChangeLog({
        templateId: payload.templateId,
        item,
      }),
    );

    if (action.type === customParametersUpdate.type) {
      const questionId = payload.resource?.nodeId;
      if (questionId) {
        listenerApi.dispatch(questionsApi.util.invalidateTags([{ type: 'QuestionContext', id: questionId }]));
      }
    }

    refreshTemplateState(state, x.data, listenerApi);
  } else {
    toast.error(`Critical error: update did not result in an updated process id.`);
  }
}

// Evaluation edits for select options route through apply-evaluation-change, same
// eligibility-routed reasoning as rules (see the apply-rule-change listener below) — the backend
// decides whether the change lands on the question directly or as a template-scoped override.
// Every other nodeType not a single select or multiple select isn't part of that routing yet, so it falls through to
// the same blind-overwrite save the generic matcher above uses.
startListening({
  matcher: isAnyOf(setCustomEvaluation),
  async effect(action: any, listenerApi) {
    const resource = action.payload.resource;
    if (resource.nodeType !== EQuestionType.SingleSelect && resource.nodeType !== EQuestionType.MultipleSelect) {
      await performGenericTemplateUpdate(action, listenerApi);
      return;
    }

    const revision = listenerApi.getState().Template.present.revision;
    if (revision !== ETemplateRevision.Draft) {
      toast.error('Updates are only allowed on draft templates.');
      return;
    }

    // Same reasoning as the rule-change listener below — retries a genuinely concurrent edit from
    // elsewhere, not a race against another call from this same click (the early-reject checkbox
    // has its own combined listener instead, precisely to avoid needing this for itself).
    const attemptSave = () => {
      const state = listenerApi.getState();
      return listenerApi.dispatch(
        templatesApi.endpoints.applyEvaluationChange.initiate({
          templateReferenceId: state.Template.present.templateReferenceId,
          processId: state.Template.present.processId,
          questionId: resource.nodeId,
          optionId: resource.key,
          evaluation: {
            id: resource.evaluation.id,
            name: resource.evaluation.name,
            bonus_points: resource.evaluation.bonus_points,
            isDefault: resource.evaluation.isDefault,
          },
          removeRuleIds: resource.removeRuleIds,
        }),
      );
    };

    let x = await attemptSave();
    if (x.error && isStaleTemplateConflict(x.error)) {
      x = await attemptSave();
    }

    if (x.error) {
      toast.error(getErrorMessage(x.error));
      return;
    }

    const state = listenerApi.getState();
    if (x.data && x.data.template.process_id) {
      refreshProcessId(x.data, listenerApi);
      refreshTemplateState(state, x.data, listenerApi);
      // The response only carries the template, not the question — patch the Library slice's
      // cached copy directly with what we know we just saved, since nothing else ever will (see
      // updateLibraryQuestionOptionEvaluation). Harmless if this landed as a template override
      // instead of on the question directly — the override always wins the display precedence
      // anyway, so this value just sits masked and unused in that case.
      listenerApi.dispatch(
        updateLibraryQuestionOptionEvaluation({
          questionId: resource.nodeId,
          optionId: resource.key,
          evaluation: resource.evaluation.name,
          bonus_points: resource.evaluation.bonus_points,
        }),
      );
      if (resource.removeRuleIds?.length) {
        listenerApi.dispatch(
          updateLibraryQuestionRules({ questionId: resource.nodeId, removeRuleIds: resource.removeRuleIds }),
        );
      }
    } else {
      toast.error(`Critical error: update did not result in an updated process id.`);
    }
  },
});

// Rule edits (Add Rule/Edit Rules/delete rule/delete all/condition-and-action tweaks) route through
// apply-rule-change instead of the generic update above, so the backend can decide per edit whether
// it lands on the question document directly or as a template-scoped override. The early-reject
// checkbox is deliberately NOT here — it has its own listener below, calling apply-automatic-
// knockout instead, since it always changes both the rule and the evaluation together.
startListening({
  matcher: isAnyOf(
    customQuestionRuleUpdate,
    deleteCustomQuestionRuleByIndex,
    deleteAllCustomRules,
    customQuestionRuleConditionPlus,
    customQuestionRuleActionPlus,
  ),
  async effect(action: any, listenerApi) {
    // Deletions need the rule id(s) as they were BEFORE this action's reducer already removed
    // them — must be read synchronously, before any await, per getOriginalState's contract.
    const originalState = listenerApi.getOriginalState();
    const revision = originalState.Template.present.revision;

    if (revision !== ETemplateRevision.Draft) {
      toast.error('Updates are only allowed on draft templates.');
      return;
    }

    const nodeId: string = action.payload.resource.nodeId;
    const changes = computeRuleChangePayload(
      action,
      {
        customQuestionRuleUpdate: customQuestionRuleUpdate.type,
        customQuestionRuleConditionPlus: customQuestionRuleConditionPlus.type,
        customQuestionRuleActionPlus: customQuestionRuleActionPlus.type,
        deleteCustomQuestionRuleByIndex: deleteCustomQuestionRuleByIndex.type,
        deleteAllCustomRules: deleteAllCustomRules.type,
      },
      {
        updatedRuleAtIndex:
          listenerApi.getState().Template?.present?.c?.parameters?.[nodeId]?.rules?.[action.payload.resource.ruleIdx],
      },
    );

    if (!changes) return;

    // A genuinely concurrent edit (another recruiter/tab saving the same draft) can still land
    // between this read and the request reaching the backend — one retry with a freshly-read
    // processId is enough, since by the time our request fails, that write has already landed.
    const attemptSave = () => {
      const state = listenerApi.getState();
      return listenerApi.dispatch(
        templatesApi.endpoints.applyRuleChange.initiate({
          templateReferenceId: state.Template.present.templateReferenceId,
          processId: state.Template.present.processId,
          questionId: nodeId,
          upsertRules: changes.upsertRules,
          removeRuleIds: changes.removeRuleIds,
        }),
      );
    };

    let x = await attemptSave();
    if (x.error && isStaleTemplateConflict(x.error)) {
      x = await attemptSave();
    }

    if (x.error) {
      if (!toastRuleReferenceBlockedError(x.error, listenerApi.getState().Library)) {
        toast.error(getErrorMessage(x.error));
      }
      return;
    }

    const state = listenerApi.getState();
    if (x.data && x.data.template.process_id) {
      refreshProcessId(x.data, listenerApi);
      refreshTemplateState(state, x.data, listenerApi);
      // Same reasoning as the evaluation listener above — the response only carries the
      // template, not the question, and nothing else ever refreshes the Library cache.
      listenerApi.dispatch(
        updateLibraryQuestionRules({
          questionId: nodeId,
          upsertRules: changes.upsertRules,
          removeRuleIds: changes.removeRuleIds,
        }),
      );
    } else {
      toast.error(`Critical error: update did not result in an updated process id.`);
    }
  },
});

// The early-reject checkbox always changes both this option's evaluation (to Disqualified) and
// its rules together — a single apply-automatic-knockout request instead of the separate
// apply-rule-change/apply-evaluation-change calls the general editors use, so there's nothing for
// a concurrent edit to race against from this one click.
startListening({
  matcher: isAnyOf(applyAutomaticKnockout),
  async effect(action: any, listenerApi) {
    const revision = listenerApi.getState().Template.present.revision;
    if (revision !== ETemplateRevision.Draft) {
      toast.error('Updates are only allowed on draft templates.');
      return;
    }

    const resource = action.payload.resource;

    const attemptSave = () => {
      const state = listenerApi.getState();
      return listenerApi.dispatch(
        templatesApi.endpoints.applyAutomaticKnockout.initiate({
          templateReferenceId: state.Template.present.templateReferenceId,
          processId: state.Template.present.processId,
          questionId: resource.nodeId,
          optionId: resource.optionId,
          rules: [resource.rule],
        }),
      );
    };

    let x = await attemptSave();
    if (x.error && isStaleTemplateConflict(x.error)) {
      x = await attemptSave();
    }

    if (x.error) {
      if (!toastRuleReferenceBlockedError(x.error, listenerApi.getState().Library)) {
        toast.error(getErrorMessage(x.error));
      }
      return;
    }

    const state = listenerApi.getState();
    if (x.data && x.data.template.process_id) {
      refreshProcessId(x.data, listenerApi);
      refreshTemplateState(state, x.data, listenerApi);
      // The response only carries the template, not the question — same reasoning as the rule
      // and evaluation listeners above, just patching both Library fields since this call touches
      // both.
      listenerApi.dispatch(updateLibraryQuestionRules({ questionId: resource.nodeId, upsertRules: [resource.rule] }));
      listenerApi.dispatch(
        updateLibraryQuestionOptionEvaluation({
          questionId: resource.nodeId,
          optionId: resource.optionId,
          evaluation: EEvaluationType.Disqualified,
        }),
      );
    } else {
      toast.error(`Critical error: update did not result in an updated process id.`);
    }
  },
});

startListening({
  actionCreator: addQuestionToBlock,
  async effect(action, listenerApi) {
    const state = listenerApi.getState();
    const revision = state.Template.present.revision;

    const payload = action.payload;
    if (!payload.resource.question) {
      toast.warning('No question specified. Aborting.');
      return;
    }

    if (revision !== ETemplateRevision.Draft) {
      toast.error('Updates are only allowed on draft templates.');
      return;
    }

    console.info('appMiddleware.addQuestionToBlock', { payload });

    const x = await listenerApi.dispatch(
      templatesApi.endpoints.addQuestionToBlock.initiate({
        templateReferenceId: state.Template.present.templateReferenceId,
        processId: state.Template.present.processId,
        blockId: payload.resource.blockId,
        questionId: payload.resource.question.id,
      }),
    );

    if (x.error) {
      toast.error(getErrorMessage(x.error));
      return;
    }

    if (x.data && x.data.template.process_id) {
      refreshProcessId(x.data, listenerApi);
      refreshTemplateState(state, x.data, listenerApi);
    } else {
      toast.error(`Critical error: update did not result in an updated process id.`);
    }
  },
});

startListening({
  actionCreator: removeQuestionFromBlock,
  async effect(action, listenerApi) {
    const state = listenerApi.getState();
    const revision = state.Template.present.revision;

    const payload = action.payload;
    if (!payload.resource.nodeId) {
      toast.warning('No question specified. Aborting.');
      return;
    }

    if (revision !== ETemplateRevision.Draft) {
      toast.error('Updates are only allowed on draft templates.');
      return;
    }

    console.info('appMiddleware.removeQuestionFromBlock', { payload });

    const x = await listenerApi.dispatch(
      templatesApi.endpoints.removeQuestionFromBlock.initiate({
        templateReferenceId: state.Template.present.templateReferenceId,
        processId: state.Template.present.processId,
        blockId: payload.resource.blockId,
        questionId: payload.resource.nodeId,
      }),
    );

    if (x.error) {
      toast.error(getErrorMessage(x.error));
      return;
    }

    if (x.data && x.data.template.process_id) {
      refreshProcessId(x.data, listenerApi);
      refreshTemplateState(state, x.data, listenerApi);
    } else {
      toast.error(`Critical error: update did not result in an updated process id.`);
    }
  },
});

const refreshProcessId = (data: TUpdatedTemplateResponse, listenerApi: Dispatcher) => {
  listenerApi.dispatch(
    updateProcessId({
      processId: data.template.process_id,
    }),
  );
};

export const refreshTemplateState = (state: RootState, data: TUpdatedTemplateResponse, listenerApi: Dispatcher) => {
  if (data.template) {
    listenerApi.dispatch(
      addTemplate({
        templateId: state.Template.present.cTemplateId,
        // TODO: would be nice to be able to just replace template
        template: {
          ...state.Template.present.c,
          process_id: data.template.process_id,
          outline: data.template.outline,
          parameters: data.template.parameters,
          blocks: data.template.blocks,
          interview_summary: data.template.interview_summary,
          change_log: data.template.change_log,
          templateOutline: data.templateOutline,
          evaluationSummary: data.evaluationSummary,
          evaluation: data.template.evaluation,
        },
        audienceProblems: data.audienceProblems,
      }),
    );
  }

  if (data.questionBelongToAnotherClient?.length) {
    listenerApi.dispatch(
      updateSwapOptionsToLibrary({ questionBelongToAnotherClient: data.questionBelongToAnotherClient }),
    );
  }

  toast.success('Template updated.');
};

// Matches TemplateBuilder's RevisionErrors.WrongProcessId (backend, 409) — the "someone else
// already saved over you" conflict, identified by status rather than its message text.
const isStaleTemplateConflict = (x: TAxiosBaseResponseError | SerializedError) =>
  'error' in x && x.error?.status === 409;

const getErrorMessage = (x: TAxiosBaseResponseError | SerializedError) => {
  console.info('getErrorMessage', { error: x });
  if ('message' in x && x.message) return x.message;
  if ('error' in x && 'message' in x.error) return x.error.message;
  if ('data' in x && typeof x.data === 'string') return x.data;
  return `${x}`;
};

/**
 * Set current template language side effects
 * When setting the current template language, set the current language
 * of all questions in the library.
 */
appMiddleware.startListening({
  matcher: isAnyOf(setCurrentTemplateLanguage),
  effect: async (action: any, listenerApi: any) => {
    const state = listenerApi.getState();
    const Library = state.Library;
    const language = action.payload.language;

    Object.keys(Library).map((key: string) => {
      if (key !== 'language') {
        listenerApi.dispatch(
          setCurrentQuestionLanguage({
            questionId: key,
            language,
          }),
        );
      }
    });
  },
});

export default appMiddleware;
